import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { api } from "@/lib/api"

// ───────────────────────────────────────────────────────────────────────
// Backend contracts (matched to legacy admin's POSTs):
//
//   POST /admin/user_list   { page, limit, search?, sortyBy?, sortDirection? }
//                           → { success, data: { docs, totalDocs, page, limit, totalPages, ... }, message }
//
//   POST /admin/update_user { id, is_subscribe, subscription_expiry?, plan? }
//                           → { success, data: <updated_user>, message }
//
//   POST /admin/delete_user { id }
//                           → { success, message }
//
// NOTE: backend expects the typo `sortyBy` (extra "y"). Preserved exactly.
// ───────────────────────────────────────────────────────────────────────

export type AdminUser = {
  _id: string
  name?: string
  email?: string
  profile_pic?: string
  is_verified?: boolean
  is_subscribe?: boolean
  auth_type?: "google" | "facebook" | "apple" | "email_password" | string
  created_at?: string
  subscription_expiry?: string | null
  plan?: string
}

export type UserListParams = {
  page: number
  limit: number
  search?: string
  /** Backend typo preserved: extra "y". */
  sortyBy?: string
  sortDirection?: 1 | -1
}

export type UserListResponse = {
  docs: AdminUser[]
  totalDocs: number
  page: number
  limit: number
  totalPages: number
}

const usersListKey = (p: UserListParams) => ["admin", "users", "list", p] as const

export function useUsersList(params: UserListParams) {
  return useQuery({
    queryKey: usersListKey(params),
    queryFn: async (): Promise<UserListResponse> => {
      const res = await api.post<{
        success: boolean
        data: UserListResponse
        message: string
      }>("/admin/user_list", params)
      // Defensive: backend has been seen returning {} or [] in error paths.
      const data = res.data?.data
      return {
        docs: Array.isArray(data?.docs) ? data.docs : [],
        totalDocs: typeof data?.totalDocs === "number" ? data.totalDocs : 0,
        page: typeof data?.page === "number" ? data.page : params.page,
        limit: typeof data?.limit === "number" ? data.limit : params.limit,
        totalPages: typeof data?.totalPages === "number" ? data.totalPages : 0,
      }
    },
    placeholderData: (prev) => prev,
  })
}

type UpdateUserSubscribePayload = {
  id: string
  is_subscribe: true
  /** ISO date string or Date — sent as-is in the legacy admin. */
  subscription_expiry: string | Date
  plan: string
}

type CancelUserSubscribePayload = {
  id: string
  is_subscribe: false
}

export function useUpdateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (
      payload: UpdateUserSubscribePayload | CancelUserSubscribePayload
    ) => {
      const res = await api.post("/admin/update_user", payload)
      return res.data
    },
    onSuccess: () => {
      // Any list query becomes stale once a user changes — invalidate the
      // whole users namespace.
      qc.invalidateQueries({ queryKey: ["admin", "users"] })
    },
  })
}

/**
 * Admin-driven email-verification override. Sets is_verified=true on the
 * user record. Used when a user can't receive the verification email
 * (typo, deliverability hiccup) and needs an admin to unblock them.
 *
 * Backend: POST /admin/verify_user { id } → { success, data, message }
 * Idempotent — safe to re-run on an already-verified user.
 */
export function useVerifyUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post("/admin/verify_user", { id })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] })
    },
  })
}

export function useDeleteUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post("/admin/delete_user", { id })
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "users"] })
    },
  })
}
